import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { updateContainer, openContainer, deleteContainer, addToStock, removeFromStock, removeProduct } from '../../actions/inventory';
import { 
    Package, 
    Lock, 
    Unlock, 
    Settings, 
    Trash2, 
    Plus, 
    Minus,
    AlertTriangle,
    CheckCircle,
    XCircle,
    Tag,
    Shield,
    ShieldOff,
    Box,
    DoorOpen,
    DoorClosed
} from 'lucide-react';

const Container = (props) => {
    const [container, setContainer] = useState({});
    const [showModal, setShowModal] = useState(false);
    const [modalConfig, setModalConfig] = useState({ title: '', message: '', type: '', action: null });
    const [isProcessing, setIsProcessing] = useState(false);

    if (JSON.stringify(props.container) !== JSON.stringify(container)) {
        setContainer({ ...props.container });
    }

    const showConfirmation = (title, message, type, action) => {
        setModalConfig({ title, message, type, action });
        setShowModal(true);
    };

    const handleConfirm = async () => {
        setIsProcessing(true);
        try {
            await modalConfig.action();
        } finally {
            setIsProcessing(false);
            setShowModal(false);
        }
    };

    const populateProducts = () => {
        if (props.brands) {
            return Object.keys(props.brands).map((key, index) => (
                <option key={`product-${index}`} value={key}>
                    {props.brands[key]}
                </option>
            ));
        }
        return [];
    };

    const onChangeContainerID = e => {
        setContainer({ ...container, [e.target.name]: e.target.value });
        let payload = {
            vendingMachine: props.vendingMachine._id,
            container: props.container._id,
            data: {
                container_id: e.target.value,
            }
        };
        props.updateContainer(payload);
    };

    const onProductSelect = (e) => {
        setContainer({ ...container, [e.target.name]: e.target.value });
        let prevProduct = container.product;
        let payload = {
            vendingMachine: props.vendingMachine._id,
            container: props.container._id,
            data: {
                product: e.target.value,
                _id: container._id,
                prevProduct: prevProduct
            }
        };
        props.updateContainer(payload);
    };

    const updateContainerWorkingStatus = (e) => {
        setContainer({ ...container, [e.target.name]: e.target.value });
        let payload = {
            container: props.container._id,
            data: {
                'isWorking': e.target.value
            }
        };
        props.updateContainer(payload);
    };

    const updateContainerReservedStatus = (e) => {
        setContainer({ ...container, [e.target.name]: (e.target.value === 'true') });
        let payload = {
            container: props.container._id,
            data: {
                'isReserved': e.target.value
            }
        };
        props.updateContainer(payload);
    };

    const openContainerAction = () => {
        let payload = { 
            vendingMachineId: props.vendingMachine.vendingMachineId, 
            containers: [props.container.containerId] 
        };
        props.openContainer(payload);
    };

    const deleteContainerAction = () => {
        var payload = {
            vendingMachine: props.vendingMachine._id, 
            container: container._id,
        };
        props.deleteContainer(payload);
    };

    const addStock = () => {
        let payload = { id: container._id, vendingMachine: container.vendingMachine };
        props.addToStock(payload);
    };

    const removeStock = () => {
        let payload = { id: container._id, vendingMachine: container.vendingMachine };
        props.removeFromStock(payload);
    };

    const removeProductAction = () => {
        let payload = { _id: container._id };
        props.removeProduct(payload, container.vendingMachine);
    };

    const getStatusBadge = (isWorking, isReserved) => {
        if (!isWorking) {
            return <span className="badge bg-danger">Disabled</span>;
        }
        if (isReserved) {
            return <span className="badge bg-warning">Reserved</span>;
        }
        return <span className="badge bg-success">Working</span>;
    };

    const getLockIcon = (isLocked) => {
        return isLocked ? 
            <Lock size={16} className="text-success me-1" /> : 
            <Unlock size={16} className="text-danger me-1" />;
    };

    const getStockIcon = (isEmpty) => {
        return isEmpty ? 
            <XCircle size={16} className="text-danger me-1" /> : 
            <CheckCircle size={16} className="text-success me-1" />;
    };

    return (
        <>
            <div className="col-xl-4 col-lg-6 col-md-12 mb-4">
                <div className="card shadow-sm h-100 border-0">
                    {/* Card Header */}
                    <div className="card-header bg-primary text-white d-flex justify-content-between align-items-center">
                        <div className="d-flex align-items-center">
                            <Package size={20} className="me-2" />
                            <span className="fw-bold">Container {container.containerId}</span>
                        </div>
                        <button
                            className="btn btn-outline-light btn-sm"
                            onClick={() => showConfirmation(
                                "Delete Container",
                                `Are you sure you want to delete container ${container.containerId}?`,
                                "danger",
                                deleteContainerAction
                            )}
                        >
                            <Trash2 size={14} className="me-1" />
                            Delete
                        </button>
                    </div>

                    <div className="card-body">
                        {/* Container ID */}
                        <div className="mb-3">
                            <label className="form-label fw-bold small">
                                <Tag size={14} className="me-1" />
                                Container ID
                            </label>
                            <input
                                className="form-control form-control-sm"
                                type="text"
                                placeholder="Container ID"
                                name="container_id"
                                value={container.containerId || ''}
                                onChange={onChangeContainerID}
                            />
                        </div>

                        {/* Door Status */}
                        <div className="mb-3">
                            <div className="d-flex justify-content-between align-items-center">
                                <span className="small fw-bold text-muted">Door Status</span>
                                <div className="d-flex align-items-center">
                                    {getLockIcon(container.isLocked)}
                                    <span className={container.isLocked ? 'text-success' : 'text-danger'}>
                                        {container.isLocked ? 'Locked' : 'Unlocked'}
                                    </span>
                                </div>
                            </div>
                        </div>

                        {/* Working Status */}
                        <div className="mb-3">
                            <label className="form-label fw-bold small">
                                <Settings size={14} className="me-1" />
                                Status
                            </label>
                            <div className="d-flex align-items-center gap-2">
                                <select
                                    className="form-select form-select-sm flex-grow-1"
                                    value={container.isWorking}
                                    name="isWorking"
                                    disabled={container.isReserved}
                                    onChange={updateContainerWorkingStatus}
                                >
                                    <option value="true">Working</option>
                                    <option value="false">Disabled</option>
                                </select>
                                {getStatusBadge(container.isWorking, container.isReserved)}
                            </div>
                        </div>

                        {/* Product Assignment */}
                        <div className="mb-3">
                            <div className="d-flex justify-content-between align-items-center mb-2">
                                <label className="form-label fw-bold small mb-0">
                                    <Box size={14} className="me-1" />
                                    Product
                                </label>
                                {container.product && (
                                    <button
                                        className="btn btn-outline-danger btn-sm"
                                        onClick={() => showConfirmation(
                                            "Remove Product",
                                            "Remove product assignment from this container?",
                                            "warning",
                                            removeProductAction
                                        )}
                                        disabled={!container.isEmpty || !container.isWorking}
                                    >
                                        <Minus size={12} className="me-1" />
                                        Remove
                                    </button>
                                )}
                            </div>
                            
                            <select
                                className="form-select form-select-sm"
                                value={container.product || ""}
                                name="product"
                                disabled={!container.isEmpty || !container.isWorking}
                                onChange={onProductSelect}
                            >
                                <option value="">Select Product</option>
                                {populateProducts()}
                            </select>
                            
                            {container.product ? (
                                <div className="mt-2">
                                    <span className="badge bg-info bg-opacity-10 text-info">
                                        {props.brands[container.product]}
                                    </span>
                                </div>
                            ) : (
                                <small className="text-danger">No product assigned</small>
                            )}
                        </div>

                        {/* Reserved Status */}
                        <div className="mb-3">
                            <label className="form-label fw-bold small">
                                <Shield size={14} className="me-1" />
                                Reservation
                            </label>
                            <div className="d-flex align-items-center gap-2">
                                <select
                                    className="form-select form-select-sm flex-grow-1"
                                    disabled={container.isEmpty || !container.isWorking}
                                    value={container.isReserved}
                                    name="isReserved"
                                    onChange={updateContainerReservedStatus}
                                >
                                    <option value="false">Available</option>
                                    <option value="true">Reserved</option>
                                </select>
                                {container.isReserved ? (
                                    <ShieldOff size={16} className="text-warning" />
                                ) : (
                                    <Shield size={16} className="text-success" />
                                )}
                            </div>
                        </div>

                        {/* Stock Management */}
                        <div className="mb-3">
                            <div className="d-flex justify-content-between align-items-center">
                                <span className="small fw-bold text-muted">Stock Status</span>
                                <div className="d-flex align-items-center">
                                    {getStockIcon(container.isEmpty)}
                                    <span className={container.isEmpty ? 'text-danger' : 'text-success'}>
                                        {container.isEmpty ? 'Empty' : 'In Stock'}
                                    </span>
                                </div>
                            </div>
                            
                            {container.product && (
                                <div className="mt-2">
                                    <button
                                        className={`btn btn-sm w-100 ${container.isEmpty ? 'btn-success' : 'btn-warning'}`}
                                        onClick={() => {
                                            if (container.isEmpty) {
                                                showConfirmation(
                                                    "Add Stock",
                                                    "Add product to this container?",
                                                    "success",
                                                    addStock
                                                );
                                            } else {
                                                showConfirmation(
                                                    "Remove Stock",
                                                    "Remove product from this container?",
                                                    "warning",
                                                    removeStock
                                                );
                                            }
                                        }}
                                        disabled={container.isReserved || !container.isWorking}
                                    >
                                        {container.isEmpty ? (
                                            <>
                                                <Plus size={14} className="me-1" />
                                                Add to Stock
                                            </>
                                        ) : (
                                            <>
                                                <Minus size={14} className="me-1" />
                                                Remove Stock
                                            </>
                                        )}
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Card Footer */}
                    <div className="card-footer bg-light">
                        <button
                            className="btn btn-primary w-100"
                            onClick={() => showConfirmation(
                                container.isLocked ? "Open Container" : "Close Container",
                                `${container.isLocked ? 'Open' : 'Close'} container ${container.containerId}?`,
                                "primary",
                                openContainerAction
                            )}
                        >
                            {container.isLocked ? (
                                <>
                                    <DoorOpen size={16} className="me-2" />
                                    Open Container
                                </>
                            ) : (
                                <>
                                    <DoorClosed size={16} className="me-2" />
                                    Close Container
                                </>
                            )}
                        </button>
                    </div>
                </div>
            </div>

            {/* Confirmation Modal */}
            {showModal && (
                <div className="modal fade show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title d-flex align-items-center">
                                    {modalConfig.type === 'danger' && <AlertTriangle size={20} className="me-2 text-danger" />}
                                    {modalConfig.type === 'warning' && <AlertTriangle size={20} className="me-2 text-warning" />}
                                    {modalConfig.type === 'success' && <CheckCircle size={20} className="me-2 text-success" />}
                                    {modalConfig.type === 'primary' && <Settings size={20} className="me-2 text-primary" />}
                                    {modalConfig.title}
                                </h5>
                                <button
                                    type="button"
                                    className="btn-close"
                                    onClick={() => setShowModal(false)}
                                ></button>
                            </div>
                            <div className="modal-body">
                                <p className="mb-0">{modalConfig.message}</p>
                            </div>
                            <div className="modal-footer">
                                <button
                                    type="button"
                                    className="btn btn-secondary"
                                    onClick={() => setShowModal(false)}
                                    disabled={isProcessing}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    className={`btn btn-${modalConfig.type === 'danger' ? 'danger' : modalConfig.type === 'warning' ? 'warning' : modalConfig.type === 'success' ? 'success' : 'primary'}`}
                                    onClick={handleConfirm}
                                    disabled={isProcessing}
                                >
                                    {isProcessing ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm me-2"></span>
                                            Processing...
                                        </>
                                    ) : (
                                        'Confirm'
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

Container.propTypes = {
    vendingMachine: PropTypes.object,
    container: PropTypes.object.isRequired,
    brands: PropTypes.object,
    updateContainer: PropTypes.func.isRequired,
    openContainer: PropTypes.func.isRequired,
    deleteContainer: PropTypes.func.isRequired,
    addToStock: PropTypes.func.isRequired,
    removeFromStock: PropTypes.func.isRequired,
    removeProduct: PropTypes.func.isRequired
};

const mapStateToProps = state => ({
    vendingMachine: state.vendingMachine.vendingMachine
});

export default connect(mapStateToProps, {
    updateContainer, openContainer, deleteContainer, addToStock,
    removeFromStock, removeProduct
})(Container);
